import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Save, Eye, Pencil, ExternalLink } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { RenderInformativa } from "@/pages/Privacy";

export default function InformativaPrivacy() {
  // Tipo di informativa attualmente in modifica. I dati del titolare sono
  // comuni a tutti (la parrocchia è la stessa); il testo è per tipo.
  const [tipo, setTipo] = useState("iscritti");
  const [tipi, setTipi] = useState([]);
  const [testo, setTesto] = useState(null);
  const [originale, setOriginale] = useState("");
  const [campi, setCampi] = useState([]);
  const [campiOrig, setCampiOrig] = useState("");
  const [versione, setVersione] = useState(null);
  const [aggiornato, setAggiornato] = useState(null);
  const [anteprima, setAnteprima] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setTesto(null);
    api.get(`/titolare?tipo=${tipo}`).then((r) => {
      setTesto(r.data.modello || "");
      setOriginale(r.data.modello || "");
      setCampi(r.data.campi || []);
      setCampiOrig(JSON.stringify(r.data.campi || []));
      setVersione(r.data.versione_attiva);
      setAggiornato(r.data.pubblicata_il);
      setTipi(r.data.tipi || []);
    });
  }, [tipo]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const setCampo = (chiave, valore) =>
    setCampi((cs) => cs.map((c) => (c.chiave === chiave ? { ...c, valore } : c)));

  const vuoti = campi.filter((c) => !String(c.valore || "").trim());

  const salva = async () => {
    if (!testo.trim()) return toast.error("Il testo non può essere vuoto");
    setSaving(true);
    try {
      const titolare = Object.fromEntries(campi.map((c) => [c.chiave, c.valore || ""]));
      const { data } = await api.put(`/informative/${tipo}`, { testo, titolare });
      setOriginale(testo);
      setCampiOrig(JSON.stringify(campi));
      setVersione(data.versione);
      setAggiornato(data.pubblicata_il);
      toast.success(`Pubblicata la versione ${data.versione}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (testo === null) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  const modificato = testo !== originale || JSON.stringify(campi) !== campiOrig;

  // Anteprima: mostra il testo COME sarà pubblicato, con i valori già sostituiti.
  const testoRisolto = campi.reduce(
    (t, c) => (String(c.valore || "").trim() ? t.split(c.segnaposto).join(c.valore.trim()) : t),
    testo
  );

  return (
    <div data-testid="informativa-privacy-page" className="max-w-3xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-500" /> Informativa Privacy
          </h1>
          <p className="text-sm text-slate-500 font-semibold">
            {versione ? `Versione ${versione} in vigore` : "Non ancora pubblicata"}
          </p>
        </div>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 text-xs" data-testid="apri-pubblica-btn">
          <ExternalLink className="h-3.5 w-3.5" /> Vedi pagina pubblica
        </a>
      </div>

      {/* Selettore del tipo. Sono documenti distinti, non traduzioni: quello
          per gli operatori minorenni è scritto per un quattordicenne, quello
          per i contatti di emergenza si rivolge a chi non ha compilato nulla. */}
      <div className="flex gap-1.5 flex-wrap mb-5" data-testid="selettore-tipo">
        {tipi.map((t) => (
          <button key={t.chiave} type="button"
            onClick={() => { if (t.chiave !== tipo) setTipo(t.chiave); }}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${
              t.chiave === tipo ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-400"}`}
            data-testid={`tipo-${t.chiave}`}>
            {t.etichetta}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${
              t.chiave === tipo ? "bg-white/20"
                : t.versione ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
              {t.versione ? `v${t.versione}` : "assente"}
            </span>
          </button>
        ))}
      </div>

      {!versione && (
        <div className="rounded-3xl bg-amber-50 border border-amber-200 p-4 mb-5" data-testid="avviso-non-pubblicata">
          <p className="text-sm font-bold text-slate-800 mb-1">Questa informativa non è ancora pubblicata</p>
          <p className="text-xs text-slate-700 font-medium leading-relaxed">
            Finché manca, i moduli che la richiedono non si possono inviare: il riquadro di
            presa visione mostra «Informativa non disponibile» e il pulsante resta bloccato.
            {tipo !== "iscritti" && " Incolla qui sotto il testo, compila i dati del titolare e pubblica."}
          </p>
        </div>
      )}

      {/* Dati del titolare: tenuti fuori dal testo così, quando cambia il
          parroco o un recapito, non si rimette mano all'intera informativa.
          Alla pubblicazione vengono sostituiti nei segnaposto. */}
      <div className="card-app p-5 mb-4" data-testid="titolare-card">
        <h2 className="font-display font-extrabold text-slate-900 mb-1">Dati del titolare del trattamento</h2>
        <p className="text-xs text-slate-500 font-semibold mb-4">
          Compilati automaticamente nel testo al momento della pubblicazione. Finché
          uno resta vuoto, l'informativa non può essere pubblicata.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          {campi.map((c) => {
            const vuoto = !String(c.valore || "").trim();
            return (
              <div key={c.chiave} className={c.chiave === "titolare_sede" ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-bold text-slate-700 mb-1" htmlFor={`campo-${c.chiave}`}>
                  {c.etichetta}
                  {vuoto && <span className="ml-1.5 text-red-500">da compilare</span>}
                </label>
                <input
                  id={`campo-${c.chiave}`}
                  className={`input-app ${vuoto ? "!border-red-300 !bg-red-50" : ""}`}
                  value={c.valore || ""}
                  placeholder={c.esempio}
                  onChange={(e) => setCampo(c.chiave, e.target.value)}
                  data-testid={`titolare-${c.chiave}`}
                />
                <p className="text-[11px] text-slate-400 font-semibold mt-1">{c.aiuto}</p>
              </div>
            );
          })}
        </div>

        {vuoti.length > 0 && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl p-3 font-semibold mt-4" data-testid="titolare-mancanti">
            Mancano {vuoti.length} camp{vuoti.length === 1 ? "o" : "i"}: {vuoti.map((c) => c.etichetta).join(", ")}.
            Finché non sono compilati la pagina pubblica continua a mostrare il modello di default.
          </p>
        )}
      </div>

      <div className="card-app p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setAnteprima(false)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${!anteprima ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="tab-modifica">
              <Pencil className="h-3.5 w-3.5" /> Modifica
            </button>
            <button type="button" onClick={() => setAnteprima(true)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${anteprima ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="tab-anteprima">
              <Eye className="h-3.5 w-3.5" /> Anteprima
            </button>
          </div>
          {aggiornato && (
            <p className="text-[11px] text-slate-400 font-semibold">
              Versione {versione} pubblicata il {new Date(aggiornato).toLocaleDateString("it-IT")}
            </p>
          )}
        </div>

        {!anteprima && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl p-3 font-semibold mb-3">
            Qui si modifica il MODELLO: i segnaposto tra parentesi quadre [ ] vengono
            sostituiti con i dati del titolare al momento della pubblicazione. Non è un
            parere legale: fai validare il testo da chi di competenza.
          </p>
        )}

        {anteprima ? (
          <div className="rounded-2xl border border-slate-100 p-4" data-testid="privacy-anteprima">
            {/* Testo COME sarà pubblicato: segnaposto già sostituiti */}
            <RenderInformativa testo={testoRisolto} />
          </div>
        ) : (
          <>
            <textarea
              className="input-app min-h-[420px] font-mono text-xs leading-relaxed"
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              data-testid="privacy-testo-input"
            />
            <p className="text-[11px] text-slate-400 font-semibold mt-2">
              Suggerimento: una riga che inizia con <code className="bg-slate-100 px-1 rounded">#</code> diventa un titolo di sezione; lascia una riga vuota tra un paragrafo e l'altro.
            </p>
          </>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            className="btn-primary"
            onClick={salva}
            disabled={saving || !modificato || vuoti.length > 0 || !testo.trim()}
            title={vuoti.length > 0 ? "Compila prima i dati del titolare" : undefined}
            data-testid="privacy-salva-btn"
          >
            <Save className="h-4 w-4" />
            {saving ? "Pubblicazione..." : versione ? "Pubblica nuova versione" : "Pubblica versione 1"}
          </button>
          {modificato && (
            <button type="button" className="btn-ghost" onClick={() => setTesto(originale)} data-testid="privacy-annulla-btn">
              Annulla modifiche
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
